/**
 * Support Assistant Per-Page Configuration
 *
 * Defines quick questions and default messages for each page in the application.
 * Questions are context-aware based on:
 * - Scope: Brand-level vs Location-level
 * - Page: Current route/page
 * - Role: Admin, Staff, Member (future enhancement)
 */

export interface QuickQuestion {
  id: string
  label: string // Short label for button (i18n key)
  text: string // Full question text to send (i18n key)
  scopes?: ('brand' | 'location')[] // Which scopes this question applies to. If omitted, applies to both
  roles?: ('admin' | 'staff' | 'member')[] // Which roles can see this question. If omitted, all roles
}

export interface PageConfig {
  defaultMessage: string // i18n key for welcome message
  quickQuestions: QuickQuestion[]
}

export type SupportAssistantConfig = Record<string, PageConfig>

/**
 * Per-page configuration
 *
 * Keys match route paths from the router
 * Use i18n keys for all user-facing text
 */
export const supportAssistantConfig: SupportAssistantConfig = {
  // Dashboard / Home
  '/': {
    defaultMessage: 'core:supportAssistant.pages.dashboard.welcome',
    quickQuestions: [
      {
        id: 'dash-reports',
        label: 'core:supportAssistant.pages.dashboard.questions.reports.label',
        text: 'core:supportAssistant.pages.dashboard.questions.reports.text',
        scopes: ['brand'], // Brand-level only
      },
      {
        id: 'dash-attendance',
        label: 'core:supportAssistant.pages.dashboard.questions.attendance.label',
        text: 'core:supportAssistant.pages.dashboard.questions.attendance.text',
        scopes: ['location'], // Location-level only
      },
      {
        id: 'dash-billing',
        label: 'core:supportAssistant.pages.dashboard.questions.billing.label',
        text: 'core:supportAssistant.pages.dashboard.questions.billing.text',
      },
    ],
  },

  // Location Settings
  '/settings/location-settings': {
    defaultMessage: 'core:supportAssistant.pages.locationSettings.welcome',
    quickQuestions: [
      {
        id: 'loc-hours',
        label: 'core:supportAssistant.pages.locationSettings.questions.hours.label',
        text: 'core:supportAssistant.pages.locationSettings.questions.hours.text',
        scopes: ['location'],
      },
      {
        id: 'loc-rooms',
        label: 'core:supportAssistant.pages.locationSettings.questions.rooms.label',
        text: 'core:supportAssistant.pages.locationSettings.questions.rooms.text',
        scopes: ['location'],
      },
      {
        id: 'loc-branding',
        label: 'core:supportAssistant.pages.locationSettings.questions.branding.label',
        text: 'core:supportAssistant.pages.locationSettings.questions.branding.text',
        scopes: ['location'],
      },
    ],
  },

  // Scheduler Settings
  '/settings/scheduler-settings': {
    defaultMessage: 'core:supportAssistant.pages.schedulerSettings.welcome',
    quickQuestions: [
      {
        id: 'sched-create',
        label: 'core:supportAssistant.pages.schedulerSettings.questions.create.label',
        text: 'core:supportAssistant.pages.schedulerSettings.questions.create.text',
        scopes: ['location'],
      },
      {
        id: 'sched-cancel',
        label: 'core:supportAssistant.pages.schedulerSettings.questions.cancel.label',
        text: 'core:supportAssistant.pages.schedulerSettings.questions.cancel.text',
        scopes: ['location'],
      },
      {
        id: 'sched-capacity',
        label: 'core:supportAssistant.pages.schedulerSettings.questions.capacity.label',
        text: 'core:supportAssistant.pages.schedulerSettings.questions.capacity.text',
        scopes: ['location'],
      },
    ],
  },

  // Account Settings
  '/settings/account-settings': {
    defaultMessage: 'core:supportAssistant.pages.accountSettings.welcome',
    quickQuestions: [
      {
        id: 'acct-password',
        label: 'core:supportAssistant.pages.accountSettings.questions.password.label',
        text: 'core:supportAssistant.pages.accountSettings.questions.password.text',
      },
      {
        id: 'acct-notifications',
        label: 'core:supportAssistant.pages.accountSettings.questions.notifications.label',
        text: 'core:supportAssistant.pages.accountSettings.questions.notifications.text',
      },
      {
        id: 'acct-profile',
        label: 'core:supportAssistant.pages.accountSettings.questions.profile.label',
        text: 'core:supportAssistant.pages.accountSettings.questions.profile.text',
      },
    ],
  },

  // Retail Settings
  '/settings/retail-settings': {
    defaultMessage: 'core:supportAssistant.pages.retailSettings.welcome',
    quickQuestions: [
      {
        id: 'retail-products',
        label: 'core:supportAssistant.pages.retailSettings.questions.products.label',
        text: 'core:supportAssistant.pages.retailSettings.questions.products.text',
        scopes: ['location'],
      },
      {
        id: 'retail-inventory',
        label: 'core:supportAssistant.pages.retailSettings.questions.inventory.label',
        text: 'core:supportAssistant.pages.retailSettings.questions.inventory.text',
        scopes: ['location'],
      },
      {
        id: 'retail-sales',
        label: 'core:supportAssistant.pages.retailSettings.questions.sales.label',
        text: 'core:supportAssistant.pages.retailSettings.questions.sales.text',
        scopes: ['location'],
      },
    ],
  },

  // Control Center
  '/control-center': {
    defaultMessage: 'core:supportAssistant.pages.controlCenter.welcome',
    quickQuestions: [
      {
        id: 'cc-templates',
        label: 'core:supportAssistant.pages.controlCenter.questions.templates.label',
        text: 'core:supportAssistant.pages.controlCenter.questions.templates.text',
        scopes: ['brand'],
      },
      {
        id: 'cc-integrations',
        label: 'core:supportAssistant.pages.controlCenter.questions.integrations.label',
        text: 'core:supportAssistant.pages.controlCenter.questions.integrations.text',
        scopes: ['brand'],
      },
      {
        id: 'cc-branding',
        label: 'core:supportAssistant.pages.controlCenter.questions.branding.label',
        text: 'core:supportAssistant.pages.controlCenter.questions.branding.text',
        scopes: ['brand'],
      },
    ],
  },

  // User Access
  '/user-access': {
    defaultMessage: 'core:supportAssistant.pages.userAccess.welcome',
    quickQuestions: [
      {
        id: 'ua-add-staff',
        label: 'core:supportAssistant.pages.userAccess.questions.addStaff.label',
        text: 'core:supportAssistant.pages.userAccess.questions.addStaff.text',
        roles: ['admin'],
      },
      {
        id: 'ua-permissions',
        label: 'core:supportAssistant.pages.userAccess.questions.permissions.label',
        text: 'core:supportAssistant.pages.userAccess.questions.permissions.text',
        roles: ['admin'],
      },
      {
        id: 'ua-remove',
        label: 'core:supportAssistant.pages.userAccess.questions.remove.label',
        text: 'core:supportAssistant.pages.userAccess.questions.remove.text',
        roles: ['admin'],
      },
    ],
  },

  // Mobile Setup
  '/mobile-setup': {
    defaultMessage: 'core:supportAssistant.pages.mobileSetup.welcome',
    quickQuestions: [
      {
        id: 'mobile-customize',
        label: 'core:supportAssistant.pages.mobileSetup.questions.customize.label',
        text: 'core:supportAssistant.pages.mobileSetup.questions.customize.text',
        scopes: ['brand'],
      },
      {
        id: 'mobile-branding',
        label: 'core:supportAssistant.pages.mobileSetup.questions.branding.label',
        text: 'core:supportAssistant.pages.mobileSetup.questions.branding.text',
        scopes: ['brand'],
      },
      {
        id: 'mobile-deploy',
        label: 'core:supportAssistant.pages.mobileSetup.questions.deploy.label',
        text: 'core:supportAssistant.pages.mobileSetup.questions.deploy.text',
        scopes: ['brand'],
      },
    ],
  },

  // Audit History
  '/control-center/audit-history': {
    defaultMessage: 'core:supportAssistant.pages.auditHistory.welcome',
    quickQuestions: [
      {
        id: 'audit-find-change',
        label: 'core:supportAssistant.pages.auditHistory.questions.findChange.label',
        text: 'core:supportAssistant.pages.auditHistory.questions.findChange.text',
        scopes: ['brand'],
      },
      {
        id: 'audit-user-actions',
        label: 'core:supportAssistant.pages.auditHistory.questions.userActions.label',
        text: 'core:supportAssistant.pages.auditHistory.questions.userActions.text',
        scopes: ['brand'],
      },
      {
        id: 'audit-export',
        label: 'core:supportAssistant.pages.auditHistory.questions.export.label',
        text: 'core:supportAssistant.pages.auditHistory.questions.export.text',
        scopes: ['brand'],
      },
    ],
  },
}

/**
 * Get configuration for current page with scope filtering
 *
 * @param pathname - Current route pathname (from useLocation)
 * @param scopeLevel - Current scope ('brand' | 'location')
 * @param userRole - Current user role ('admin' | 'staff' | 'member')
 * @returns PageConfig with filtered questions (empty array if no page config found)
 */
export function getPageConfig(
  pathname: string,
  scopeLevel: 'brand' | 'location',
  userRole?: 'admin' | 'staff' | 'member'
): PageConfig {
  // Try exact match first
  let config = supportAssistantConfig[pathname]

  // If no exact match, try matching by prefix (for nested routes)
  if (!config) {
    const matchingPath = Object.keys(supportAssistantConfig).find((path) =>
      pathname.startsWith(path)
    )

    config = matchingPath ? supportAssistantConfig[matchingPath] : undefined
  }

  // If no config found, return empty questions
  if (!config) {
    return {
      defaultMessage: 'core:supportAssistant.welcomeMessage',
      quickQuestions: [],
    }
  }

  // Filter questions by scope and role
  const filteredQuestions = config.quickQuestions.filter((q) => {
    // Check scope filter
    if (q.scopes && !q.scopes.includes(scopeLevel)) {
      return false
    }

    // Check role filter
    if (q.roles && userRole && !q.roles.includes(userRole)) {
      return false
    }

    return true
  })

  return {
    ...config,
    quickQuestions: filteredQuestions,
  }
}

/**
 * Get page display name from pathname
 * Maps route paths to user-friendly names
 */
export function getPageDisplayName(pathname: string): string {
  const pageNames: Record<string, string> = {
    '/': 'Dashboard',
    '/c': 'Dashboard',
    '/c/': 'Dashboard',
    '/settings/location-settings': 'Location Settings',
    '/settings/scheduler-settings': 'Scheduler Settings',
    '/settings/account-settings': 'Account Settings',
    '/settings/retail-settings': 'Retail Settings',
    '/control-center': 'Control Center',
    '/control-center/audit-history': 'Audit History',
    '/user-access': 'User Access',
    '/mobile-setup': 'Mobile Setup',
  }

  if (/^\/s\/[^/]+\/?$/.test(pathname)) {
    return pageNames['/']
  }

  // Try exact match
  if (pageNames[pathname]) {
    return pageNames[pathname]
  }

  // Try prefix match
  const matchingPath = Object.keys(pageNames).find(
    (path) => pathname.startsWith(path) && path !== '/'
  )

  if (matchingPath) {
    return pageNames[matchingPath]
  }

  // Fallback: capitalize last segment
  const segments = pathname.split('/').filter(Boolean)
  const lastSegment = segments[segments.length - 1] || 'Dashboard'
  return lastSegment
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}
